import { Fragment, useEffect, useState } from "react"
import { BaseTable, Card, FooterMenu, HeaderMenu, Loading, TableHeader } from "components"
import { FormProvider, useForm } from "react-hook-form"
import FilterBugetManager from "./components/filter-buget-manager"
import { useApi, useHandleAuth, useToggle } from "utils"
import { bugetMapperPayloadMapper, calculateParent, generateNetProfit, handleExportExcelBugetManager, useExportPdf } from "./utils"
import moment from "moment"
import { CoaNameEnum } from "./enum"
import { TableBody, TableTotalCOA } from "./components"
import { BalanceSheetNoneType, BalanceSheetReportType, FilterBugetManager as FilterBugetManagerType } from "./types"

export default function BugetVaranceListPage() {
  const api = useApi()
  const auth = useHandleAuth()
  const token = auth.userTokenData()
  const toggleZeroValue = useToggle(true)
  const toggleAccountNumber = useToggle(true)
  const toggleSubHeader = useToggle(true)
  const toggleDetail = useToggle(true)

  const [reportData, setReportData] = useState<Array<BalanceSheetReportType>>([])

  const defaultValues: FilterBugetManagerType = {
    AccountGroup: "0",
    start_range: moment().startOf("year").format("YYYYMM"),
    end_range: moment().endOf("year").format("YYYYMM"),
    currency: "1",
  }
  const methods = useForm<FilterBugetManagerType>({ defaultValues: defaultValues })
  const isFetching = methods.formState.isSubmitting
  const [date, dateTo] = methods.watch(["start_range", "end_range"])

  const handleExport = () => handleExportExcelBugetManager({
    data: reportData,
    companyName: token?.company_name ?? "",
    date: methods.getValues().start_range,
    dateTo: methods.getValues().end_range
  })

  const exportPdf = useExportPdf({
    data: reportData,
  })


  const onFilterSubmit = async (data: FilterBugetManagerType) => {
    const isFilterAll = Boolean(data.AccountGroup === "0")

    await api.postForm<{
      payload: {
        data_budget_variance: BalanceSheetNoneType[],
      }
    }>("/budgetmanager/get_budget_variance", bugetMapperPayloadMapper(data))
      .then((res) => {
        const payload = res.data.payload.data_budget_variance as BalanceSheetNoneType[]
        const calculate = calculateParent(payload)

        const filterByCoaGrup = isFilterAll
          ? generateNetProfit(calculate)
          : payload.filter((coa) => coa.group_coa_code === data.AccountGroup)

        const mapBalanceToArray = filterByCoaGrup.map((d) => ({
          ...d,
          actual: [Number(d.actual) || 0],
          variance: [Number(d.variance) || 0],
          budget: [Number(d.budget) || 0],
          percent_variance: [Number(d.percent_variance) || 0],
        }));
        setReportData(mapBalanceToArray);
      })
  }

  useEffect(() => {
    if (methods.formState.submitCount === 0) {
      methods.handleSubmit((data) => onFilterSubmit(data))()
    }
    // eslint-disable-next-line
  }, [])


  return (
    <Fragment>
      <section className="container my-5">
        <HeaderMenu title="BUDGET VARIANCE" />

        <FormProvider {...methods}>
          <FilterBugetManager
            toggleDetail={toggleDetail}
            toggleSubHeader={toggleSubHeader}
            toggleZeroValue={toggleZeroValue}
            toggleAccountNumber={toggleAccountNumber}
            onFilterSubmit={(data) => onFilterSubmit(data)}
          />
        </FormProvider>

        <section className="mt-2">
          <Card className="min-h-[calc(100vh-645px)]">
            <Card.Body>
              <section className="my-5 container  overflow-x-auto">
                <div className="mb-3 font-bold">
                  <div>PT HAWK</div>
                  <div className="capitalize">
                    {`${moment(date).format("MMMM YYYY")} - ${moment(dateTo).format("MMMM YYYY")}`}
                  </div>
                </div>

                <div className="overflow-x-auto max-h-[calc(100vh-600px)]">
                  <BaseTable className="border-spacing-0">
                    <TableHeader>
                      <tr className="uppercase">
                        <th>CHART OF ACCOUNT</th>
                        <th>{`${moment(date).format("MM/YYYY")} - ${moment(dateTo).format("MM/YYYY")}`}</th>
                        <th>{`${moment(date).format("MM/YYYY")} - ${moment(dateTo).format("MM/YYYY")}`}</th>
                        <th>VARIANCE</th>
                        <th>VARIANCE %</th>
                      </tr>
                    </TableHeader>
                    <tbody>
                      {!reportData?.length || isFetching ? (
                        <tr>
                          <td colSpan={10} className="text-center">
                            <Loading loading={isFetching} errorText={isFetching ? "" : "No data available"} />
                          </td>
                        </tr>
                      ) : (
                        reportData.map((item, _key: number) => item.parent_id === -1 && (
                          <>
                            <TableBody
                              key={item.coa_id}
                              list={reportData}
                              item={item}
                              displayAccountNumber={toggleAccountNumber.isActive}
                              displaySubHeader={toggleSubHeader.isActive}
                              displayDetail={toggleDetail.isActive}
                            />
                            {item.coa_name !== CoaNameEnum.NET_PROFIT && (<TableTotalCOA item={item} />)}
                          </>
                        )
                        )
                      )}
                    </tbody>
                  </BaseTable>
                </div>
              </section>
            </Card.Body>
          </Card>
        </section>

        <div className="mt-2">
          <FooterMenu
            export={{ actiontype: "export", onClick: handleExport }}
            pdf={{
              actiontype: "pdf",
              onClick: () => exportPdf.handleExportPdf({
                date: methods.getValues().start_range,
                endDate: methods.getValues().end_range,
                displayAccountNumber: false
              })
            }}
          />
        </div>
      </section>
    </Fragment>
  )
}
